import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ChipProps = Omit<ComponentProps<'button'>, 'value'> & {
  /** Leading icon (decorative; hidden from assistive tech). */
  icon?: ReactNode;
  /** The chip's name, e.g. "Metrics", "Filters". */
  label?: ReactNode;
  /** The current value, rendered in medium weight after the label. */
  value?: ReactNode;
  /** A count badge (e.g. active filters). Hidden when 0 or undefined. */
  count?: number;
};

/**
 * A compact popover trigger that shows what is currently chosen:
 * "[icon] Label <b>value</b> (count)". Use as the child of
 * `<PopoverTrigger asChild>`; Radix supplies aria-expanded, which styles the
 * open state. Name the popover it opens with `aria-label` on PopoverContent.
 */
export function Chip({ icon, label, value, count, className, children, type, ...props }: ChipProps) {
  return (
    <button
      type={type ?? 'button'}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-ctl border border-line bg-surface px-2.5 text-sm transition-[background-color,border-color] duration-150 hover:border-line-strong hover:bg-wash aria-expanded:border-line-strong aria-expanded:bg-wash disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    >
      {icon ? <span aria-hidden="true" className="inline-flex text-fg">{icon}</span> : null}
      {label}
      {/* Whitespace text nodes are invisible in the flex row but keep the accessible name readable ("Metrics 4"). */}
      {value !== undefined && value !== null ? <>{' '}<b className="font-medium">{value}</b></> : null}
      {children}
      {count ? (
        <>
          {' '}
          <span aria-hidden="true" className="num inline-grid h-[18px] min-w-[18px] place-items-center rounded-pill bg-blue px-[5px] text-2xs font-semibold text-on-blue">
            {count}
          </span>
          <span className="sr-only">({count})</span>
        </>
      ) : null}
    </button>
  );
}
